"use client";
import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import mammoth from "mammoth";
import { useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  DocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { assessmentsBus } from "../utils/assessmentsBus";
import FullScreenLoader from "../components/FullScreenLoader";
import { motion, AnimatePresence } from "framer-motion";
import Alert from "../components/Alert";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import dayjs from "dayjs";
import {
  TrashIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  CalendarDateRangeIcon,
  ExclamationTriangleIcon,
  MicrophoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PaperClipIcon,
} from "@heroicons/react/24/solid";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const DETAILS_API =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessment";
const POLICY_TIMER_KEY = "bv.aiPolicyPoll";
const ASSESS_TIMER_KEY = "bv.assessmentPoll";

const MicrophoneIconView = ({ url, className = "w-3 h-3" }) => {
  return (
    <MicrophoneIcon
      className={`${className} text-pink-700`}
      aria-hidden="true"
    />
  );
};

const PaperClipIconView = ({ url, className = "w-3 h-3" }) => {
  return (
    <PaperClipIcon
      className={`${className} text-pink-700`}
      aria-hidden="true"
    />
  );
};

/* ---------------------- Submenu: Assessments (tab bar) ---------------------- */
function AssessmentsMenu({ items }) {
  const [open, setOpen] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const wrapRef = useRef(null);
  const barRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      //setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Arrow key navigation between tabs
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      setActiveIdx((idx) => {
        const next =
          e.key === "ArrowRight"
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length;
        const btn =
          barRef.current?.querySelectorAll("button[role='tab']")?.[next];
        btn?.focus();
        return next;
      });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, items.length]);

  return (
    <div>
      {/* Full-width tab bar under header */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="assessments-bar"
            ref={wrapRef}
            id="assessments-tabbar"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-0 top-[-2%] z-50 mt-3 w-screen -translate-x-1/2"
          >
            {/* Bar background */}
            <div className="bg-[#0b162c]/95 backdrop-blur-md border-t border-white/10 shadow-[0_6px_14px_rgba(0,0,0,0.35)]">
              {/* Align to page container */}
              <div className="mx-auto w-full max-w-[1200px]">
                <div
                  ref={barRef}
                  role="tablist"
                  aria-label="Assessments"
                  className="hidden sm:block flex gap-6 overflow-x-auto py-3 sm:py-3.5 text-center justify-center"
                >
                  {items.map((it, idx) => {
                    //const active = idx === activeIdx;
                    return (
                      <button
                        key={it.label}
                        role="tab"
                        aria-selected={it?.active}
                        tabIndex={0}
                        className={[
                          "relative pb-2 text-base sm:text-lg whitespace-nowrap transition xs:w-full sm:w-[15%] mr-5",
                          it?.active
                            ? "text-[#ffc49b]"
                            : "text-slate-100/90 hover:text-white",
                        ].join(" ")}
                      >
                        {it.label}
                        {/* active underline */}
                        <span
                          className={[
                            "absolute left-0 right-0 -bottom-0.5 h-[2px]",
                            it?.active ? "bg-[#ffc49b]" : "bg-transparent",
                          ].join(" ")}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Carousel: buttons only (no swipe/drag) + index control ---------- */
function Carousel({ slideIndex, setSlideIndex, children, className = "" }) {
  const trackRef = useRef(null);
  const total = React.Children.count(children);

  const scrollToIndex = useCallback((idx) => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.children?.[idx];
    if (!child) return;

    const left = child.offsetLeft - el.offsetLeft;
    el.scrollTo({ left, behavior: "smooth" });
  }, []);

  // Block horizontal wheel scrolling (shift+wheel or trackpads)
  const handleWheel = (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault();
  };

  const go = (dir) => {
    const next = (slideIndex + dir + total) % total;
    setSlideIndex(next);
    scrollToIndex(next);
  };

  useEffect(() => {
    scrollToIndex(slideIndex);
  }, [scrollToIndex, slideIndex]);

  return (
    <div className={`relative ${className}`}>
      {/* Track (no user-driven scroll) */}
      <div
        ref={trackRef}
        onWheel={handleWheel}
        className="flex gap-0 snap-x snap-mandatory pb-2"
        style={{
          overflowX: "hidden",
          overflowY: "hidden",
          touchAction: "pan-y",
          scrollBehavior: "smooth",
        }}
      >
        {React.Children.map(children, (child, i) => (
          <div
            key={i}
            className={`snap-start shrink-0 p-2 w-full ${
              i == 0
                ? "sm:w-1/3"
                : i == 1
                  ? "sm:w-1/3"
                  : i == 2
                    ? "sm:w-1/3"
                    : i == 3
                      ? "sm:w-1/3"
                      : i == 4
                        ? "sm:w-1/5"
                        : i == 5
                          ? "sm:w-1/5"
                          : i == 6
                            ? "sm:w-1/2"
                            : i == 7
                              ? "sm:w-1/2"
                              : i == 8
                                ? "sm:w-1/2"
                                : "sm:w-full"
            }`}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Prev / Next */}
      <button
        type="button"
        onClick={() => go(-1)}
        className="md:flex items-center justify-center absolute left-0  top-1/2 -translate-y-1/2 h-9 w-9 rounded-full text-white shadow"
        aria-label="Previous"
      >
        <ChevronLeftIcon
          aria-hidden="true"
          className="size-10 sm:size-20 text-white"
        />
      </button>

      <button
        type="button"
        onClick={() => go(1)}
        className="md:flex items-center justify-center absolute right-0  top-1/2 -translate-y-1/2 h-9 w-9 rounded-full text-white shadow"
        aria-label="Next"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className="size-10 sm:size-20 text-white"
        />
      </button>
    </div>
  );
}

Carousel.Item = function CarouselItem({ children }) {
  return children;
};

const formatTime = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

function ScheduleCalendar({
  selectedDate,
  onSelectDate,
  selectedInter,
  interviewSlots = [],
}) {
  // Month currently being viewed
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? dayjs(selectedDate) : dayjs(),
  );

  const todayStr = dayjs().format("YYYY-MM-DD");
  const selectedStr = selectedDate
    ? dayjs(selectedDate).format("YYYY-MM-DD")
    : null;

  const days = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");

    // We want weeks starting Monday.
    // JS day(): 0 = Sunday, 1 = Monday, ... 6 = Saturday
    // Convert so Monday = 0, Sunday = 6
    const startWeekday = (startOfMonth.day() + 6) % 7;

    // First cell in the grid (6 weeks * 7 days = 42 cells)
    const gridStart = startOfMonth.subtract(startWeekday, "day");

    const totalCells = 6 * 7;

    const result = [];

    for (let i = 0; i < totalCells; i++) {
      const d = gridStart.add(i, "day");
      const iso = d.format("YYYY-MM-DD");

      const events = [];
      interviewSlots.forEach((slot, idx) => {
        if (!slot?.date || !slot?.time) return;
        const slotIso = dayjs(slot.date).format("YYYY-MM-DD");
        if (slotIso !== iso) return;

        const [hours, minutes] = slot.time.split(":").map(Number);
        const slotTime = dayjs()
          .hour(hours || 0)
          .minute(minutes || 0);
        const label = `${
          selectedInter?.name || "Interview"
        } @ ${slotTime.format("h:mm A")}`;

        events.push({
          id: `${iso}-${idx}`,
          name: label,
        });
      });

      result.push({
        date: iso,
        jsDate: d.toDate(),
        isCurrentMonth: d.month() === currentMonth.month(),
        isToday: iso === todayStr,
        events,
      });
    }

    return result;
  }, [currentMonth, todayStr, interviewSlots, selectedInter]);

  const handlePrevMonth = () => {
    setCurrentMonth((m) => m.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth((m) => m.add(1, "month"));
  };

  const handleSelectDay = (day) => {
    onSelectDate(day.date);
  };

  return (
    <div className="w-full max-w-5xl bg-white text-gray-900 rounded-sm">
      {/* header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold">
          <time dateTime={currentMonth.format("YYYY-MM")}>
            {currentMonth.format("MMMM YYYY")}
          </time>
        </h2>
        <div className="flex items-center rounded-md bg-white shadow-sm outline outline-1 -outline-offset-1 outline-gray-300">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="flex h-8 w-8 items-center justify-center rounded-l-md text-gray-400 hover:text-gray-500 hover:bg-gray-50"
          >
            <span className="sr-only">Previous month</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            className="flex h-8 w-8 items-center justify-center rounded-r-md text-gray-400 hover:text-gray-500 hover:bg-gray-50"
          >
            <span className="sr-only">Next month</span>
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* weekday labels */}
      <div className="grid grid-cols-7 gap-px border-b border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-700">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
          <div key={i} className="bg-white py-2">
            {d}
          </div>
        ))}
      </div>

      {/* calendar grid */}
      <div className="flex bg-gray-100 text-xs text-gray-700">
        <div className="grid w-full grid-cols-7 grid-rows-6 gap-px">
          {days.map((day) => {
            const isSelected = day.date === selectedStr;

            return (
              <button
                key={day.date}
                type="button"
                onClick={() => handleSelectDay(day)}
                data-is-current-month={day.isCurrentMonth ? "" : undefined}
                data-is-selected={isSelected ? "" : undefined}
                className={`group relative px-3 py-2 text-left text-primary h-20
                  hover:bg-primary hover:text-white font-bold text-md
                  ${!day.isCurrentMonth ? "opacity-50" : ""}
                  ${day.isToday && !isSelected ? "ring-1 ring-primary" : ""}
                  ${isSelected ? "font-semibold bg-primary text-white" : ""}`}
              >
                <time dateTime={day.date} className="relative text-md">
                  {day.jsDate.getDate()}
                </time>

                {selectedInter?.interviewSlots?.length > 0 ? (
                  <ol className="mt-2 space-y-1">
                    {selectedInter?.interviewSlots?.map((event) =>
                      event?.date === day.date ? (
                        <li
                          key={event.id}
                          className="text-[11px] font-bold pl-4"
                        >
                          {event?.confirmed ? (
                            <CheckCircleIcon className="w-4  absolute left-2" />
                          ) : (
                            <ExclamationTriangleIcon className="w-4  absolute left-2" />
                          )}
                          {selectedInter?.name} @ {event.time}
                        </li>
                      ) : null,
                    )}
                  </ol>
                ) : day.events.length > 0 ? (
                  <ol className="mt-2 space-y-1">
                    {day.events.map((event) => (
                      <li key={event.id} className="text-[11px] font-bold">
                        {event.name}
                      </li>
                    ))}
                  </ol>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function InterviewSetupNew() {
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  const [recordingFor, setRecordingFor] = useState(null); // which interviewee is recording
  const [recordSeconds, setRecordSeconds] = useState(0);

  const navigate = useNavigate();
  const [alert, setAlert] = useState({
    open: false,
    variant: "info",
    title: "",
    message: "",
    primaryAction: null,
    secondaryAction: null,
  });
  const [items, setItems] = useState([
    {
      label: "Current Assessments",
      active: true,
    },
    {
      label: "Interview Setup",
      active: true,
    },
    {
      label: "Documents",
      active: true,
    },
    {
      label: "Interviews",
      active: true,
    },
    {
      label: "Custom Chat",
      active: true,
    },
    {
      label: "Progress",
      active: true,
    },
  ]);
  const [showInfo, setShowInfo] = useState(true);
  const [showInfo1, setShowInfo1] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { company } = useParams();
  const [companyDetails, setCompanyDetails] = useState(null);
  const [intervieweeList, setIntervieweeList] = useState([]);
  const [interviewDateSelected, setInterviewDateSelected] = useState();
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsErr, setDetailsErr] = useState("");
  const [policyTimer, setPolicyTimer] = useState(() => {
    try {
      const raw = localStorage.getItem(POLICY_TIMER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [policyElapsed, setPolicyElapsed] = useState({
    minutes: 0,
    seconds: 0,
  });
  const [assessTimer, setAssessTimer] = useState(() => {
    try {
      const raw = localStorage.getItem(ASSESS_TIMER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [assessElapsed, setAssessElapsed] = useState({
    minutes: 0,
    seconds: 0,
  });

  const [files, setFiles] = useState([]);

  const [completedInterviews, setcompletedInterviews] = useState(0);
  const [pendingInterviews, setpendingInterviews] = useState(0);

  const [ieName, setIeName] = useState("");
  const [ieEmail, setIeEmail] = useState("");
  const [ieDepartment, setIeDepartment] = useState("");
  const [iePosition, setIePosition] = useState("");
  const [err, setErr] = useState("");

  // read auth token from localStorage
  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const json = JSON.parse(raw);
      return json?.authToken || null;
    } catch {
      return null;
    }
  }, []);

  const companyName = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const json = JSON.parse(raw);
      return json?.companyName || null;
    } catch {
      return null;
    }
  }, []);

  const companyLogo = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const json = JSON.parse(raw);
      return json?.companyLogo || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!company) return;
    setLoading(true);

    const controller = new AbortController();
    const run = async () => {
      setDetailsErr("");
      setDetailsLoading(true);
      setLoading(true);
      try {
        if (!authToken) {
          setDetailsErr("Not authorized.");
          setLoading(false);
          return;
        }

        setLoading(true);

        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ companyName: unslugToName(company) }),
          signal: controller.signal,
        });

        const data = await res.json().catch(() => []);
        if (!data.companyDetails) {
          setDetailsErr("No company details found.");
          setCompanyDetails(null);
          setLoading(false);
          navigate("/");
        } else {
          setCompanyDetails(data?.companyDetails);
          setIntervieweeList(data?.intervieweeList);
          setInterviewDateSelected(data?.interviewDateSelected);
          setcompletedInterviews(data?.completedInterviews);
          setpendingInterviews(data?.pendingInterviews);
          setLoading(false);
        }
        setLoading(false);
        setDetailsLoading(false);
      } catch (e) {
        if (e.name !== "AbortError") {
          setLoading(false);
          setDetailsLoading(false);
          setAlert({
            open: true,
            variant: "error",
            title: "Failed",
            message: e.message || "Failed to load company details.",
          });
        }
      }
    };

    run();
    return () => controller.abort();
  }, [company, authToken]); // ✅ correct deps

  const fetchDetails = useCallback(
    async (signal) => {
      if (!company) return;
      setErr("");
      setLoading(true);
      try {
        if (!authToken) {
          setErr("Not authorized.");
          setCompanyDetails(null);
          setIntervieweeList([]);
          return;
        }

        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken, // change to `Bearer ${authToken}` if your API expects it
          },
          body: JSON.stringify({ companyName: unslugToName(company) }),
          signal,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(data?.message || `Request failed (${res.status})`);

        setCompanyDetails(data?.companyDetails || null);
        setIntervieweeList(
          Array.isArray(data?.intervieweeList) ? data.intervieweeList : [],
        );
        setcompletedInterviews(data?.completedInterviews);
        setpendingInterviews(data?.pendingInterviews);
        setLoading(false);
      } catch (e) {
        if (e.name !== "AbortError") {
          setErr(e.message || "Failed to load details.");
        }
      }
    },
    [company, authToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchDetails(controller.signal);
    return () => controller.abort();
  }, [fetchDetails]);

  const totalToComplete = intervieweeList.length;
  const completedCount = 0; // adjust if your API returns completion status

  const handleDeleteInterviewee = async (inter) => {
    if (!authToken) {
      setErr("Not authorized.");
      return;
    }

    const assessmentId = companyDetails?.assessmentId || companyDetails?._id;
    const intervieweeId = inter?.intervieweeId || inter?._id;

    if (!assessmentId || !intervieweeId) {
      setErr("Missing identifiers to delete interviewee.");
      return;
    }

    try {
      setDeletingId(intervieweeId || inter?.email || inter?.name);

      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken, // add `Bearer ` if your API needs it
          },
          body: JSON.stringify({
            assessmentId,
            intervieweeId,
            // include extras in case backend accepts them
            name: inter?.name,
            email: inter?.email,
          }),
        },
      );

      const data = await res.json().catch(() => ({}));
      setAlert({
        open: true,
        variant: "success",
        title: "Interviewee Deleted",
        message: "Interviewee deleted successfully.",
      });
      fetchDetails();
    } catch (e) {
      setErr(e.message || "Failed to delete interviewee.");
    } finally {
      setDeletingId(null);
    }
  };

  const CLOUD_NAME = "dcfotquxt";
  const UPLOAD_PRESET = "flsdzoc2"; // must allow unsigned uploads

  // optional: disable while a widget is open
  const [uploadingFor, setUploadingFor] = useState(null);
  const [recordChoiceOpen, setRecordChoiceOpen] = useState(false);
  const recordInterRef = useRef(null);
  const [processingModal, setProcessingModal] = useState({
    open: false,
    minutes: 0,
    seconds: 0,
    title: "Generating Assessment",
  });
  const loadCloudinaryWidget = useCallback(() => {
    if (typeof window === "undefined") return Promise.reject();
    if (window.cloudinary?.createUploadWidget) {
      return Promise.resolve(window.cloudinary);
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://widget.cloudinary.com/v2.0/global/all.js";
      script.async = true;
      script.onload = () => {
        if (window.cloudinary?.createUploadWidget) {
          resolve(window.cloudinary);
        } else {
          reject(new Error("Cloudinary widget failed to load."));
        }
      };
      script.onerror = () =>
        reject(new Error("Cloudinary widget failed to load."));
      document.body.appendChild(script);
    });
  }, []);

  const patchIntervieweeFiles = async ({
    assessmentId,
    intervieweeId,
    files,
  }) => {
    setLoading(true);
    const res = await fetch(
      "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken, // or `Bearer ${authToken}` if your API expects it
        },
        body: JSON.stringify({
          assessmentId,
          intervieweeId,
          files,
          interviewStatus: files?.length > 0 ? "completed" : "pending",
        }),
      },
    );

    const data = await res.json().catch(() => ({}));
    if (data?.success) {
      setAlert({
        open: true,
        variant: "success",
        title: "Data Updated",
        message: "Data Updated successfully.",
      });
      setOpenRecordingModal(false);
      fetchDetails();
    }
    setLoading(false);
  };

  const patchAssessmentFile = async (
    assessmentId,
    assessmentFile,
    assessmentAiPolicyPdf,
  ) => {
    //console.log(assessmentId, assessmentFile);
    setLoading(true);
    const res = await fetch(
      "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessment",
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken, // or `Bearer ${authToken}` if your API expects it
        },
        body: JSON.stringify({
          assessmentId,
          assessmentFile,
          assessmentAiPolicyPdf,
          status: "Completed",
        }),
      },
    );

    const data = await res.json().catch(() => ({}));
    if (data?.success) {
      setAlert({
        open: true,
        variant: "success",
        title: "Data Updated",
        message: "Data Updated successfully.",
      });
      setOpenRecordingModal(false);
      fetchDetails();
    }
    setLoading(false);
  };

  // upload recorded audio blob to Cloudinary and then save it for the interviewee
  // Somewhere near top of file or config:
  var ASSEMBLYAI_API_KEY = "8a0cd1b76cb54e31926c3756a0665ad7"; // or however you store it

  const handleUploadPdf = useCallback(
    async (inter) => {
      const filesAssessmentId = inter?.assessmentId;
      const intervieweeId = inter?.intervieweeId || inter?._id;

      if (!filesAssessmentId || !intervieweeId) {
        setAlert({
          open: true,
          variant: "error",
          title: "Upload blocked",
          message: "Missing assessment or interviewee information.",
        });
        return;
      }

      try {
        const cloudinary = await loadCloudinaryWidget();
        const widget = cloudinary.createUploadWidget(
          {
            cloudName: CLOUD_NAME,
            uploadPreset: UPLOAD_PRESET,
            sources: ["local", "url"],
            multiple: false,
            resourceType: "raw",
            clientAllowedFormats: ["pdf", "docx", "doc"],
            maxFiles: 1,
            folder: `BizVantage/${filesAssessmentId || "unknown"}/${
              intervieweeId || "unknown"
            }/Files`,
          },
          async (error, result) => {
            if (error) {
              setAlert({
                open: true,
                variant: "error",
                title: "Upload failed",
                message: error.message || "Could not upload file.",
              });
              return;
            }

            if (result?.event === "success" && result?.info) {
              const data = result.info;

              try {
                setLoading(true);
                let fullText = "";

                const isDocx =
                  data?.secure_url?.toLowerCase().endsWith(".docx") ||
                  data?.format === "docx";

                if (isDocx) {
                  try {
                    const res = await fetch(data.secure_url);
                    const buf = await res.arrayBuffer();
                    const docxText = await mammoth.extractRawText({
                      arrayBuffer: buf,
                    });
                    fullText = docxText?.value || "";
                  } catch (docErr) {
                    console.warn("DOCX extraction failed:", docErr);
                    fullText = "";
                  }
                } else {
                  // 1) Load the PDF
                  const loadingTask = pdfjsLib.getDocument(data?.secure_url);
                  const pdf = await loadingTask.promise;

                  // 2) Loop through pages
                  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    const content = await page.getTextContent();

                    const strings = content.items.map((item) => item.str);
                    fullText += strings.join(" ") + "\n\n";
                  }
                }

                setFiles((prev) => {
                  const prevFiles = prev[intervieweeId] || inter?.files || [];
                  const nextFiles = [
                    ...prevFiles,
                    {
                      url: data.secure_url,
                      format: data.resource_type,
                      bytes: data.bytes,
                      transcript: { text: fullText.trim() },
                      public_id: data.public_id,
                    },
                  ];

                  patchIntervieweeFiles({
                    assessmentId: filesAssessmentId,
                    intervieweeId,
                    files: nextFiles,
                  }).catch((e) => console.error("PATCH failed:", e));

                  return { ...prev, [intervieweeId]: nextFiles };
                });
              } catch (err) {
                console.error(err);
                setLoading(false);
                setAlert({
                  open: true,
                  variant: "error",
                  title: "Error",
                  message: err.message || "Unable to extract text from PDF.",
                });
              } finally {
                setLoading(false);
              }
            }
          },
        );

        widget.open();
      } catch (e) {
        setAlert({
          open: true,
          variant: "error",
          title: "Upload unavailable",
          message: e.message || "Could not load upload widget.",
        });
      }
    },
    [
      CLOUD_NAME,
      UPLOAD_PRESET,
      companyDetails,
      loadCloudinaryWidget,
      patchIntervieweeFiles,
    ],
  );

  const uploadRecordingToCloudinary = async (blob, inter, id) => {
    setOpenRecordingModal(false);
    setLoading(true);
    // small helper to pause between polls
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    try {
      setUploadingFor(id);

      var assessmentId = inter?.assessmentId;
      var intervieweeId = inter?.intervieweeId || inter?._id;

      const folder = `BizVantage/${assessmentId || "unknown"}/${
        intervieweeId || "unknown"
      }/Recordings`;

      var formData = new FormData();
      formData.append("file", blob);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder", folder);

      // 1) Upload to Cloudinary
      var res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/video/upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      var data = await res.json();
      if (!res.ok || !data.secure_url) {
        setLoading(false);
        setAlert({
          open: true,
          variant: "error",
          title: "Upload failed",
          message: "Failed to upload recording.",
        });
        //throw new Error(data.error?.message || "Cloudinary upload failed");
      }

      var fileMeta = {
        url: data.secure_url,
        format: data.format,
        bytes: data.bytes,
        public_id: data.public_id,
      };

      // 2) Send to AssemblyAI for speech-to-text
      try {
        var createTranscriptRes = await fetch(
          "https://api.assemblyai.com/v2/transcript",
          {
            method: "POST",
            headers: {
              authorization: ASSEMBLYAI_API_KEY, // <-- set this in your env
              "content-type": "application/json",
            },
            body: JSON.stringify({
              audio_url: data.secure_url,
            }),
          },
        );

        const transcriptJob = await createTranscriptRes.json();

        if (!createTranscriptRes.ok || !transcriptJob.id) {
          setLoading(false);
          console.error("Failed to create transcript:", transcriptJob);
          setAlert({
            open: true,
            variant: "error",
            title: "Transcript failed",
            message: "Failed to create transcript.",
          });
          //throw new Error(transcriptJob.error || "Failed to create transcript");
        }

        // 3) Poll until transcript is ready (or fails / times out)
        let transcriptText = null;
        let status = transcriptJob.status;
        const transcriptId = transcriptJob.id;

        // safety limit so we don't poll forever
        const maxAttempts = 20;
        let attempts = 0;

        while (
          (status === "queued" || status === "processing") &&
          attempts < maxAttempts
        ) {
          attempts += 1;
          await sleep(5000); // wait 5 seconds between polls

          const pollRes = await fetch(
            `https://api.assemblyai.com/v2/transcript/${transcriptId}`,
            {
              method: "GET",
              headers: {
                authorization: ASSEMBLYAI_API_KEY,
              },
            },
          );

          const pollData = await pollRes.json();
          status = pollData.status;

          if (status === "completed") {
            transcriptText = pollData;
            break;
          }

          if (status === "error") {
            setLoading(false);
            console.error("Transcription error:", pollData.error);
            setAlert({
              open: true,
              variant: "error",
              title: "Transcript failed",
              message: "Failed to create transcript.",
            });
            //throw new Error(pollData.error || "Transcription failed");
          }
        }

        if (!transcriptText && attempts >= maxAttempts) {
          setLoading(false);
          setAlert({
            open: true,
            variant: "error",
            title: "Transcript failed",
            message: "Transcription polling timed out.",
          });
          console.warn("Transcription polling timed out");
        }

        // 4) If we got a transcript, attach it to fileMeta
        if (transcriptText) {
          fileMeta.transcript = transcriptText;
        }
      } catch (transcriptionError) {
        setLoading(false);
        // Don't kill the whole upload if transcription fails;
        // just log it and continue with fileMeta without transcript.
        setAlert({
          open: true,
          variant: "error",
          title: "Transcript failed",
          message: "Transcription failed.",
        });
        console.error("AssemblyAI transcription failed:", transcriptionError);
      }

      var filesAssessmentId = assessmentId;

      // 5) Update local state and PATCH backend with transcript included (if we got it)
      setFiles((prev) => {
        const nextFiles = [...prev, fileMeta];
        patchIntervieweeFiles({
          assessmentId: inter?.assessmentId,
          intervieweeId,
          files: nextFiles,
        }).catch((e) => console.error("PATCH failed:", e));

        return { ...prev, [intervieweeId]: nextFiles };
      });
      setLoading(false);
      setAlert({
        open: true,
        variant: "success",
        title: "Recording Uploaded",
        message: "Audio interview uploaded successfully.",
      });
    } catch (e) {
      setLoading(false);
      console.error(e);
      setAlert({
        open: true,
        variant: "error",
        title: "Upload failed",
        message: e.message || "Failed to upload recording.",
      });
    } finally {
      setLoading(false);
      setUploadingFor(null);
      setOpenRecordingModal(false);
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;

    mr.stop();
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  const startRecording = async (inter, micOnly) => {
    let displayStream = null;
    let micStream = null;
    let audioCtx = null;
    let mixedStream = null;
    try {
      const id =
        inter?.intervieweeId || inter?._id || inter?.email || inter?.name;

      // Capture microphone always
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      if (micOnly) {
        mixedStream = micStream;
      } else {
        // Ask the user to share a tab/window/screen with system audio enabled.
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: true, // required by getDisplayMedia
        });
        // Stop video tracks (we only need audio).
        displayStream.getVideoTracks().forEach((t) => t.stop());

        // Mix system audio + mic into a single audio stream.
        audioCtx = new AudioContext();
        const dest = audioCtx.createMediaStreamDestination();

        const displayAudioTracks = displayStream.getAudioTracks();
        if (displayAudioTracks.length) {
          const displaySource = audioCtx.createMediaStreamSource(
            new MediaStream([displayAudioTracks[0]]),
          );
          displaySource.connect(dest);
        }

        const micAudioTracks = micStream.getAudioTracks();
        if (micAudioTracks.length) {
          const micSource = audioCtx.createMediaStreamSource(
            new MediaStream([micAudioTracks[0]]),
          );
          micSource.connect(dest);
        }

        mixedStream = dest.stream;
      }

      const mr = new MediaRecorder(mixedStream);
      mediaRecorderRef.current = mr;
      recordChunksRef.current = [];

      mr.ondataavailable = (e) =>
        e.data.size && recordChunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(recordChunksRef.current, { type: "audio/webm" });
        mixedStream?.getTracks().forEach((t) => t.stop());
        displayStream?.getTracks().forEach((t) => t.stop());
        micStream?.getTracks().forEach((t) => t.stop());
        audioCtx?.close().catch(() => {});
        uploadRecordingToCloudinary(blob, inter, id);
        setRecordingFor(null);
        setRecordSeconds(0);
      };

      mr.start();
      setRecordingFor(id);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(
        () => setRecordSeconds((s) => s + 1),
        1000,
      );
    } catch (e) {
      console.error(e);
      mixedStream?.getTracks().forEach((t) => t.stop());
      displayStream?.getTracks().forEach((t) => t.stop());
      micStream?.getTracks().forEach((t) => t.stop());
      audioCtx?.close().catch(() => {});
      setAlert({
        open: true,
        variant: "error",
        title: "Recording failed",
        message:
          e.message ||
          "Could not start recording. Try sharing screen with system audio.",
      });
      setRecordingFor(null);
      setRecordSeconds(0);
    }
  };

  // Click handler for the REC button
  const handleRecordClick = (inter) => {
    const id =
      inter?.intervieweeId || inter?._id || inter?.email || inter?.name;

    if (recordingFor === id) {
      // stop current recording for this interviewee
      stopRecording();
    } else if (!recordingFor) {
      // prompt for recording mode, then start
      recordInterRef.current = inter;
      setRecordChoiceOpen(true);
    } else {
      // already recording someone else – you can decide what to do here
      // e.g. ignore, or stop previous and start new
    }
  };

  const startProcessingDialog = useCallback(
    (title = "Generating Assessment") => {
      setProcessingModal({ open: true, minutes: 0, seconds: 0, title });
    },
    [],
  );

  const stopProcessingDialog = useCallback(() => {
    setProcessingModal({
      open: false,
      minutes: 0,
      seconds: 0,
      title: "Generating Assessment",
    });
  }, []);

  const getCompanyNameForPolling = useCallback(() => {
    return (
      policyTimer?.companyName ||
      assessTimer?.companyName ||
      companyDetails?.companyName ||
      companyName ||
      (company ? unslugToName(company) : null)
    );
  }, [
    policyTimer?.companyName,
    assessTimer?.companyName,
    companyDetails?.companyName,
    companyName,
    company,
  ]);

  const handleDeleteAssessmentDocument = useCallback(
    async (kind) => {
      const assessmentId = companyDetails?.assessmentId;
      if (!authToken) {
        setAlert({
          open: true,
          variant: "error",
          title: "Unable to delete",
          message: "Missing company or authorization info.",
        });
        return;
      }

      const ok = window.confirm("This can't be undone. Remove this file?");
      if (!ok) return;

      try {
        setLoading(true);
        const body =
          kind === "policy"
            ? { assessmentId: assessmentId, assessmentAiPolicyPdf: "" }
            : { assessmentId: assessmentId, assessmentFile: "" };

        const res = await fetch(DETAILS_API, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || "Delete failed");

        setCompanyDetails((prev) => ({
          ...(prev || {}),
          ...(kind === "policy"
            ? { assessmentAiPolicyPdf: "" }
            : { assessmentFile: "" }),
        }));

        setAlert({
          open: true,
          variant: "success",
          title: "Removed",
          message: "File removed successfully.",
        });
      } catch (e) {
        setAlert({
          open: true,
          variant: "error",
          title: "Delete failed",
          message: e.message || "Could not remove the file.",
        });
      } finally {
        setLoading(false);
      }
    },
    [authToken, getCompanyNameForPolling],
  );

  useEffect(() => {
    let t = null;
    if (processingModal.open) {
      t = setInterval(() => {
        setProcessingModal((prev) => {
          const nextSeconds = prev.seconds + 1;
          if (nextSeconds >= 60) {
            return {
              ...prev,
              minutes: prev.minutes + 1,
              seconds: 0,
            };
          }
          return { ...prev, seconds: nextSeconds };
        });
      }, 1000);
    }
    return () => t && clearInterval(t);
  }, [processingModal.open]);

  const startPolicyPolling = useCallback(() => {
    const nameForPoll = getCompanyNameForPolling();
    if (!nameForPoll) return;
    const payload = { startedAt: Date.now(), companyName: nameForPoll };
    setPolicyTimer(payload);
    try {
      localStorage.setItem(POLICY_TIMER_KEY, JSON.stringify(payload));
    } catch {}
  }, [getCompanyNameForPolling]);

  const stopPolicyPolling = useCallback(() => {
    setPolicyTimer(null);
    setPolicyElapsed({ minutes: 0, seconds: 0 });
    try {
      localStorage.removeItem(POLICY_TIMER_KEY);
    } catch {}
  }, []);

  const startAssessPolling = useCallback(() => {
    const nameForPoll = getCompanyNameForPolling();
    if (!nameForPoll) return;
    const payload = { startedAt: Date.now(), companyName: nameForPoll };
    setAssessTimer(payload);
    try {
      localStorage.setItem(ASSESS_TIMER_KEY, JSON.stringify(payload));
    } catch {}
  }, [getCompanyNameForPolling]);

  const stopAssessPolling = useCallback(() => {
    setAssessTimer(null);
    setAssessElapsed({ minutes: 0, seconds: 0 });
    try {
      localStorage.removeItem(ASSESS_TIMER_KEY);
    } catch {}
  }, []);

  // track elapsed timer since policy polling started
  useEffect(() => {
    if (!policyTimer?.startedAt) {
      setPolicyElapsed({ minutes: 0, seconds: 0 });
      return;
    }
    const updateElapsed = () => {
      const diff = Date.now() - policyTimer.startedAt;
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setPolicyElapsed({ minutes, seconds });
    };
    updateElapsed();
    const id = setInterval(updateElapsed, 1000);
    return () => clearInterval(id);
  }, [policyTimer?.startedAt]);

  useEffect(() => {
    if (!assessTimer?.startedAt) {
      setAssessElapsed({ minutes: 0, seconds: 0 });
      return;
    }
    const updateElapsed = () => {
      const diff = Date.now() - assessTimer.startedAt;
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setAssessElapsed({ minutes, seconds });
    };
    updateElapsed();
    const id = setInterval(updateElapsed, 1000);
    return () => clearInterval(id);
  }, [assessTimer?.startedAt]);

  // poll assessment API every 5s until AI Policy PDF is available
  useEffect(() => {
    if (!policyTimer?.startedAt) return;
    if (!authToken) return;

    let stopped = false;
    const poll = async () => {
      const nameForPoll = getCompanyNameForPolling();
      if (!nameForPoll) return;
      try {
        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ companyName: nameForPoll }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.companyDetails) {
          setCompanyDetails((prev) => data.companyDetails || prev);
        }
        const pdf = data?.companyDetails?.assessmentAiPolicyPdf;
        if (pdf) {
          stopPolicyPolling();
          setAlert({
            open: true,
            variant: "success",
            title: "AI Policy Ready",
            message: "AI Policy & Guidelines assessment has completed.",
          });
          stopped = true;
        }
      } catch (e) {
        // swallow errors and retry on next tick
      }
    };

    poll();
    const id = setInterval(() => {
      if (!stopped) poll();
    }, 10000);
    return () => clearInterval(id);
  }, [
    policyTimer?.startedAt,
    getCompanyNameForPolling,
    authToken,
    stopPolicyPolling,
  ]);

  // poll assessment API every 10s until Assessment PDF is available
  useEffect(() => {
    if (!assessTimer?.startedAt) return;
    if (!authToken) return;

    let stopped = false;
    const poll = async () => {
      const nameForPoll = getCompanyNameForPolling();
      if (!nameForPoll) return;
      try {
        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ companyName: nameForPoll }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.companyDetails) {
          setCompanyDetails((prev) => data.companyDetails || prev);
        }
        const pdf = data?.companyDetails?.assessmentFile;
        if (pdf) {
          stopAssessPolling();
          setAlert({
            open: true,
            variant: "success",
            title: "Assessment Ready",
            message: "Assessment Findings document is available.",
          });
          stopped = true;
        }
      } catch (e) {
        // swallow and retry
      }
    };

    poll();
    const id = setInterval(() => {
      if (!stopped) poll();
    }, 10000);
    return () => clearInterval(id);
  }, [
    assessTimer?.startedAt,
    getCompanyNameForPolling,
    authToken,
    stopAssessPolling,
  ]);

  // Background poll by assessmentId every 10s until files present
  useEffect(() => {
    const assessmentIdVal = companyDetails?.assessmentId || companyDetails?._id;
    if (!assessmentIdVal) return;
    if (!authToken) return;
    if (!policyTimer?.startedAt && !assessTimer?.startedAt) return;

    let stopped = false;
    const pollById = async () => {
      try {
        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ assessmentId: assessmentIdVal }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.companyDetails) {
          setCompanyDetails((prev) => data.companyDetails || prev);
        }
        const hasAssess = !!data?.companyDetails?.assessmentFile;
        const hasPolicy = !!data?.companyDetails?.assessmentAiPolicyPdf;
        if (hasAssess) stopAssessPolling();
        if (hasPolicy) stopPolicyPolling();
        if (hasAssess || hasPolicy) {
          stopped = true;
        }
      } catch (_) {
        // ignore and continue polling
      }
    };

    pollById();
    const id = setInterval(() => {
      if (!stopped) pollById();
    }, 10000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [
    companyDetails?.assessmentId,
    companyDetails?._id,
    authToken,
    policyTimer?.startedAt,
    assessTimer?.startedAt,
    stopAssessPolling,
    stopPolicyPolling,
  ]);

  const handleRecordingChoice = useCallback((micOnly) => {
    const target = recordInterRef.current;
    setRecordChoiceOpen(false);
    recordInterRef.current = null;
    if (target) startRecording(target, micOnly);
  }, []);

  const handleAddInterviewee = async () => {
    if (!authToken) {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message: "Failed to add interviewee.",
      });
      return;
    }
    const assessmentId = companyDetails?._id; // ← or wherever you store it
    if (!assessmentId) {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message: "Failed to add interviewee.",
      });
      return;
    }
    if (!ieName.trim() || !ieEmail.trim() || !ieDepartment || !iePosition) {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message: "Please fill the fields to add interviewee.",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken, // change to `Bearer ${authToken}` if your API expects it
          },
          body: JSON.stringify({
            assessmentId: companyDetails?.assessmentId,
            name: ieName.trim(),
            email: ieEmail.trim(),
            department: ieDepartment,
            position: iePosition,
            aiPolocyGuidelines,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (data?.length > 0) {
        if (aiPolocyGuidelines == "Yes") {
          const newres = await fetch(
            "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                authorization: authToken, // change to `Bearer ${authToken}` if your API expects it
              },
              body: JSON.stringify({
                assessmentId: companyDetails?.assessmentId,
                name: ieName.trim(),
                email: ieEmail.trim(),
                department: ieDepartment,
                position: iePosition,
                aiPolocyGuidelines: "No",
              }),
            },
          );
          const newdata = await newres.json().catch(() => ({}));
          if (newdata?.length > 0) {
            setLoading(false);
            setAlert({
              open: true,
              variant: "success",
              title: "Successfully added.",
              message: "Interviwee is successfully added.",
            });
            setIntervieweeList(newdata);
          } else {
            setLoading(false);
            setAlert({
              open: true,
              variant: "error",
              title: "Already Present.",
              message: "Interviewee with this name is already present.",
            });
          }
        } else {
          setLoading(false);
          setAlert({
            open: true,
            variant: "success",
            title: "Successfully added.",
            message: "Interviwee is successfully added.",
          });
          setIntervieweeList(data);
        }
      } else {
        setLoading(false);
        setAlert({
          open: true,
          variant: "error",
          title: "Already Present.",
          message: "Interviewee with this name is already present.",
        });
      }

      // success: clear inputs (or add to a local list if you keep one)
      setIeName("");
      setIeEmail("");
      setIeDepartment("");
      setIePosition("");
      // optionally: toast/alert success here
    } catch (e) {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message: "Failed to add interviewee.",
      });
    } finally {
      setLoading(false);
    }
  };

  const [selectedIdx, setSelectedIdx] = useState(null);

  // helper to display a nice label
  const labelFor = (item, i) => {
    if (typeof item === "string") return item || `Interviewee ${i + 1}`;
    return (
      item?.fullName ||
      [item?.firstName, item?.lastName].filter(Boolean).join(" ") ||
      item?.email ||
      item?.name ||
      `Interviewee ${i + 1}`
    );
  };

  const [slideIndex, setSlideIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("company");
  const slideLabels = useMemo(
    () => [
      "Current Assessments", // Slide 1: company details
      "Interview Setup", // Slide 2: add interviewees
      "Interviewee List", // Slide 3
      "Documents", // Slide 4
      "Upload Interviews", // Slide 5
      "Custom Chat", // Slide 6
      "Progress", // Slide 7 (Assessment Progress)
    ],
    [],
  );
  const [currLabel, setCurrLabel] = useState(slideLabels[0]);
  useEffect(() => {
    setCurrLabel(slideLabels[slideIndex] ?? "Assessment Activities");
  }, [slideIndex, slideLabels]);

  const mapIndexToTab = useCallback((idx) => {
    if (idx === 0) return "company";
    if (idx === 1) return "addInterviews";
    if (idx === 2) return "interviewees";
    if (idx === 3) return "schedule";
    if (idx >= 4 && idx <= 6) return "documents";
    if (idx === 7) return "customChat";
    if (idx === 8) return "progress";
    return "company";
  }, []);

  const handleSectionClick = useCallback((section) => {
    switch (section) {
      case "company":
        setSlideIndex(0);
        break;
      case "addInterviews":
        setSlideIndex(1);
        break;
      case "interviewees":
        setSlideIndex(2);
        break;
      case "schedule":
      case "documents":
        setSlideIndex(4);
        break;
      case "customChat":
        setSlideIndex(7);
        break;
      case "progress":
        setSlideIndex(8);
        break;
      default:
        break;
    }
    setActiveTab(section);
  }, []);

  useEffect(() => {
    setActiveTab(mapIndexToTab(slideIndex));
  }, [mapIndexToTab, slideIndex]);

  const [openRecordingModal, setOpenRecordingModal] = useState(false);
  const [selectedInter, setSelectedInter] = useState({});

  const [aiPolocyGuidelines, setAiPolocyGuidelines] = useState("No");

  const [openInterviewScheduleModal, setOpenInterviewScheduleModal] =
    useState(false);
  const [interviewSlots, setInterviewSlots] = useState([
    { date: null, time: "", confirmed: false },
    { date: null, time: "", confirmed: false },
  ]);
  const [activeSlotIdx, setActiveSlotIdx] = useState(0);

  const slotLabels = ["First Date", "Second Date"];

  const canSubmitAssessment = useMemo(() => {
    let yesCount = 0;
    let noCount = 0;
    intervieweeList?.forEach((i) => {
      const guideline = (i?.aiPolocyGuidelines || "").toLowerCase();
      if (guideline === "yes") yesCount += 1;
      if (guideline === "no") noCount += 1;
    });
    var a = [];
    for (var i = 0; i < intervieweeList?.length; i++) {
      if (
        intervieweeList[i]?.aiPolocyGuidelines === "No" &&
        intervieweeList[i]?.files?.length > 0
      ) {
        a.push(intervieweeList[i]);
      }
    }
    if (a?.length == noCount) {
      return true;
    } else {
      return false;
    }
  }, [intervieweeList]);

  const canSubmitAiPolicyAssessment = useMemo(() => {
    let yesCount = 0;
    let noCount = 0;
    intervieweeList?.forEach((i) => {
      const guideline = (i?.aiPolocyGuidelines || "").toLowerCase();
      if (guideline === "yes") yesCount += 1;
      if (guideline === "no") noCount += 1;
    });
    var a = [];
    for (var i = 0; i < intervieweeList?.length; i++) {
      if (
        intervieweeList[i]?.aiPolocyGuidelines === "Yes" &&
        intervieweeList[i]?.files?.length > 0
      ) {
        a.push(intervieweeList[i]);
      }
    }
    if (a?.length == yesCount) {
      return true;
    } else {
      return false;
    }
  }, [intervieweeList]);

  const activeSlot = interviewSlots[activeSlotIdx];
  const allSlotsReady = interviewSlots.every((slot) => slot.date && slot.time);

  const updateSlot = (idx, payload) => {
    setInterviewSlots((prev) =>
      prev.map((slot, i) => (i === idx ? { ...slot, ...payload } : slot)),
    );
  };

  const formatDateFriendly = (date) =>
    date ? dayjs(date).format("MMM DD, YYYY") : "Pick a date";

  const formatTimeFriendly = (time) => {
    if (!time) return "Pick a time";
    const [hours, minutes] = time.split(":").map(Number);
    const parsed = dayjs()
      .hour(hours || 0)
      .minute(minutes || 0);
    return parsed.format("h:mm A");
  };

  const handleConfirmSchedule = async (
    assessmentId,
    intervieweeId,
    interviewSlots,
  ) => {
    setOpenInterviewScheduleModal(false);
    setLoading(true);
    const res = await fetch(
      "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken, // or `Bearer ${authToken}` if your API expects it
        },
        body: JSON.stringify({
          assessmentId,
          intervieweeId,
          interviewSlots,
        }),
      },
    );

    const data = await res.json().catch(() => ({}));
    if (data?.success) {
      setAlert({
        open: true,
        variant: "success",
        title: "Success",
        message: "Interview slots submitted.",
      });
      setOpenInterviewScheduleModal(false);
      fetchDetails();
    }
    setLoading(false);
  };

  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [activeTranscript, setActiveTranscript] = useState("");

  const handleSubmitAssessment = async (canSubmitAssessment) => {
    if (canSubmitAssessment) {
      try {
        startProcessingDialog("Generating Ai-Assessment");

        // 1) Call FluxPrompt API
        const myHeaders = new Headers();
        myHeaders.append(
          "api-key",
          "FLUX_API.74962c2d-51b8-49a2-9592-a0fdb21cb18c", // move to ENV in real app
        );
        myHeaders.append("Content-Type", "application/json");

        var variableInputs = [
          {
            inputId: "varInputNode_1771877895447_0.3451",
            inputText: companyDetails?.assessmentId?.toString(),
          },
        ];
        variableInputs.push({
          inputId: "varInputNode_1771877902257_0.8503",
          inputText: `${companyLogo || ""}`,
        });
        variableInputs.push({
          inputId: "varInputNode_1771877913234_0.7457",
          inputText: "",
        });
        var textArr = [];
        for (var i = 0; i < intervieweeList?.length; i++) {
          if (intervieweeList[i]?.aiPolocyGuidelines == "No") {
            for (var j = 0; j < intervieweeList[i].files?.length; j++) {
              textArr.push(intervieweeList[i]?.files[j]?.transcript?.text);
            }
          }
        }
        variableInputs[2].inputText = textArr.join("$$$$$");

        var raw = JSON.stringify({
          variableInputs,
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        const response = await fetch(
          "https://api.fluxprompt.ai/flux/api-v2?flowId=4af76918-2f11-4fda-a93e-89055a1257f3",
          requestOptions,
        );

        if (!response.ok) {
          throw new Error("FluxPrompt API error");
        }

        // 2) Parse JSON response & grab PDF URL
        const fluxJson = await response.json();
        // defensive checks
        const messages = fluxJson?.data?.message || [];
        const pdfMessage = messages[0]; // you said data.message[1].text is PDF URL

        // if (!pdfMessage?.text) {
        //   throw new Error("PDF URL not found in FluxPrompt response");
        // }

        // const pdfUrl = pdfMessage.text;

        // // 3) Upload the PDF URL to Cloudinary
        // const assessmentId =
        //   companyDetails?.assessmentId || companyDetails?._id;
        // const intervieweeId =
        //   selectedInter?.intervieweeId || selectedInter?._id;

        // const folder = `BizVantage/${assessmentId || "unknown"}/${
        //   intervieweeId || "unknown"
        // }/Recordings`;

        // const formData = new FormData();
        // // For remote URL upload, "file" can be the URL string
        // formData.append("file", pdfUrl);
        // formData.append("upload_preset", UPLOAD_PRESET);
        // formData.append("folder", folder);

        // // use raw or auto since it's a PDF
        // const cloudRes = await fetch(
        //   `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/upload`,
        //   {
        //     method: "POST",
        //     body: formData,
        //   },
        // );

        // const cloudData = await cloudRes.json();
        // if (!cloudRes.ok || !cloudData.secure_url) {
        //   console.error("Cloudinary error payload:", cloudData);
        //   throw new Error(
        //     cloudData.error?.message || "Cloudinary upload of PDF failed",
        //   );
        // }

        // // 4) Log Cloudinary path (secure_url or public_id)
        // var assessmentPdf = cloudData.secure_url;
        // var assessmentAiPolicyPdf = undefined;
        // await patchAssessmentFile(
        //   assessmentId,
        //   assessmentPdf,
        //   assessmentAiPolicyPdf,
        // ).catch((e) => console.error("PATCH failed:", e));

        setAlert({
          open: true,
          variant: "success",
          title: "Success",
          message:
            "Ai Assessments generation is in-progress, once completed it willl added to the assessment.",
        });
        startAssessPolling();

        // optional: save in state / patch backend here if needed
        // e.g. patchAssessmentFiles({ assessmentId, pdfUrl: cloudData.secure_url })
      } catch (error) {
        console.error("Submit assessment error:", error);
        // show toast / alert if you have one
      } finally {
        setAlert({
          open: true,
          variant: "success",
          title: "Success",
          message:
            "Ai Assessments generation is in-progress, once completed it willl added to the assessment.",
        });
        stopProcessingDialog();
      }
    } else {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message:
          "Required document(s) AI Policy & Guidelines dosucmnet(s) missing.",
      });
    }
  };

  const handleSubmitAiPolicyAssessment = async (
    canSubmitAiPolicyAssessment,
  ) => {
    if (canSubmitAiPolicyAssessment) {
      try {
        startProcessingDialog("Generating Ai Policy & Guidelines Assessment");

        // 1) Call FluxPrompt API
        const myHeaders = new Headers();
        myHeaders.append(
          "api-key",
          "FLUX_API.74962c2d-51b8-49a2-9592-a0fdb21cb18c",
        );
        myHeaders.append("Content-Type", "application/json");

        var variableInputs = [
          {
            inputId: "varInputNode_1771611159441_0.4392",
            inputText: companyDetails?.assessmentId?.toString(),
          },
          {
            inputId: "varInputNode_1771611169663_0.4963",
            inputText: "",
          },
        ];

        var textArr = [];
        for (var i = 0; i < intervieweeList?.length; i++) {
          if (intervieweeList[i]?.aiPolocyGuidelines == "Yes") {
            for (var j = 0; j < intervieweeList[i].files?.length; j++) {
              textArr.push(intervieweeList[i]?.files[j]?.transcript?.text);
            }
          }
        }
        variableInputs[1].inputText = textArr.join("$$$$$");

        // if (companyName && companyLogo) {
        //   variableInputs.push({
        //     inputId: "varInputNode_1767981226069_0.2217",
        //     inputText: `${companyLogo}`,
        //   });
        // }

        const raw = JSON.stringify({
          variableInputs,
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        const response = await fetch(
          "https://api.fluxprompt.ai/flux/api-v2?flowId=f355fc25-6458-47bd-bde1-ea2806720a9d",
          requestOptions,
        );

        if (!response.ok) {
          throw new Error("FluxPrompt API error");
        }

        // 2) Parse JSON response & grab PDF URL
        const fluxJson = await response.json();
        // defensive checks
        const messages = fluxJson?.data?.message || [];
        const pdfMessage = messages[0]; // you said data.message[1].text is PDF URL

        if (!pdfMessage?.text) {
          throw new Error("PDF URL not found in FluxPrompt response");
        }

        // const pdfUrl = pdfMessage.text;

        // // 3) Upload the PDF URL to Cloudinary
        // const assessmentId =
        //   companyDetails?.assessmentId || companyDetails?._id;
        // const intervieweeId =
        //   selectedInter?.intervieweeId || selectedInter?._id;

        // const folder = `BizVantage/${assessmentId || "unknown"}/${
        //   intervieweeId || "unknown"
        // }/Recordings`;

        // const formData = new FormData();
        // // For remote URL upload, "file" can be the URL string
        // formData.append("file", pdfUrl);
        // formData.append("upload_preset", UPLOAD_PRESET);
        // formData.append("folder", folder);

        // // use raw or auto since it's a PDF
        // const cloudRes = await fetch(
        //   `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/upload`,
        //   {
        //     method: "POST",
        //     body: formData,
        //   },
        // );

        // const cloudData = await cloudRes.json();
        // if (!cloudRes.ok || !cloudData.secure_url) {
        //   console.error("Cloudinary error payload:", cloudData);
        //   throw new Error(
        //     cloudData.error?.message || "Cloudinary upload of PDF failed",
        //   );
        // }

        // // 4) Log Cloudinary path (secure_url or public_id)
        // var assessmentAiPolicyPdf = cloudData.secure_url;
        // var assessmentPdf = undefined;
        // await patchAssessmentFile(
        //   assessmentId,
        //   assessmentPdf,
        //   assessmentAiPolicyPdf,
        // ).catch((e) => console.error("PATCH failed:", e));

        setAlert({
          open: true,
          variant: "success",
          title: "Success",
          message:
            "Ai-Policy Guidelines generation is in-progress, once completed it willl added to the assessment.",
        });
        startPolicyPolling();

        // optional: save in state / patch backend here if needed
        // e.g. patchAssessmentFiles({ assessmentId, pdfUrl: cloudData.secure_url })
      } catch (error) {
        console.error("Submit assessment error:", error);
        // show toast / alert if you have one
      } finally {
        setAlert({
          open: true,
          variant: "success",
          title: "Success",
          message:
            "Ai-Policy Guidelines generation is in-progress, once completed it willl added to the assessment.",
        });

        stopProcessingDialog();
      }
    } else {
      setAlert({
        open: true,
        variant: "error",
        title: "Error",
        message: "Required AI Assessment document(s) missing.",
      });
    }
  };

  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showAssessmentModalFile, setShowAssessmentModalFile] = useState("");

  return (
    <div className="w-full">
      {loading && <FullScreenLoader />}

      <Alert
        open={alert.open}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        variant={alert.variant}
        title={alert.title}
        message={alert.message}
        primaryAction={alert.primaryAction}
        secondaryAction={alert.secondaryAction}
        autoCloseMs={alert.autoCloseMs}
      />

      <Dialog
        open={processingModal.open}
        onClose={() => {}}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/60" />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <DialogPanel className="bg-white w-full max-w-lg shadow-lg p-6 space-y-3 relative">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {processingModal.title}
            </DialogTitle>
            <div className=" absolute w-7 h-7 top-3 right-4 border-4 border-primary border-dashed rounded-full animate-spin text-center mx-auto"></div>

            <p className="text-sm text-gray-700">
              Assessment might take between 3 to 7 minutes, please be patient.
            </p>
            <p className="text-sm font-mono text-gray-900">
              {processingModal.minutes === 0
                ? `Elapsed: ${processingModal.seconds}s`
                : `Elapsed: ${processingModal.minutes}m ${processingModal.seconds}s`}
            </p>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog
        open={openRecordingModal}
        onClose={setOpenRecordingModal}
        className="relative"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <DialogPanel
              transition
              className="relative mx-auto w-full transform overflow-hidden rounded-lg bg-[#294563] text-white shadow-2xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-8 pt-6">
                <div className="bg-white px-6 py-3 font-semibold text-black rounded-sm">
                  Interview Recording
                </div>

                <div className="bg-white px-6 py-3 text-sm font-medium text-black rounded-sm">
                  {selectedInter?.name}:&nbsp;{" "}
                  <span className="font-semibold">
                    {selectedInter?.position} of {selectedInter?.department}
                  </span>
                </div>
              </div>

              {/* Main content */}
              <div className="px-8 pb-10 pt-10 flex gap-10 items-center">
                {/* Left: document + text */}
                <div className="flex-1 flex flex-col gap-6">
                  <div className="text-center">
                    <p className="text-sm tracking-wide">Select the document</p>
                    <div className="mx-auto mt-1 h-[2px] w-40 bg-white" />
                    <p className="mt-3 text-sm">
                      {selectedInter?.aiPolocyGuidelines == "Yes"
                        ? "Ai Policy & Guidelines"
                        : "Ai readiness assessment document"}
                    </p>
                  </div>

                  {/* Document illustration row */}
                  {selectedInter?.aiPolocyGuidelines == "Yes" ? (
                    <iframe
                      className="h-[60vh]"
                      src="https://res.cloudinary.com/dcfotquxt/image/upload/v1764874006/Ai-AssEss/AI_Policies_Guidelines_Builder_Packet.docx_v0sxxb.pdf"
                    ></iframe>
                  ) : (
                    <iframe
                      className="h-[60vh]"
                      src="https://res.cloudinary.com/dcfotquxt/image/upload/v1763613933/Ai-AssEss/GoWest_Employee_AI_Readiness_Interview_Questions_mgiqvv.pdf"
                    ></iframe>
                  )}
                </div>

                {/* Right: recording card */}
                {(() => {
                  const id =
                    selectedInter?.intervieweeId ||
                    selectedInter?._id ||
                    selectedInter?.email ||
                    selectedInter?.name;
                  const isRecording = recordingFor === id;
                  const isUploading = uploadingFor === id;

                  return (
                    <div className="flex flex-col items-center gap-4">
                      <div
                        onClick={() => {
                          setSelectedInter(selectedInter);
                          handleRecordClick(selectedInter);
                        }}
                        className={`w-[180px] h-[230px] cursor-pointer ${
                          !isRecording ? "bg-primary" : "bg-red-600"
                        } border-4 border-white flex flex-col items-center justify-center`}
                      >
                        {/* mic icon placeholder */}
                        {/* replace with your Microphone icon if you want */}
                        <MicrophoneIcon className="w-20 text-white" />

                        <div className="text-3xl font-bold tracking-widest">
                          REC
                        </div>

                        {/* timer */}
                        <div className="mt-6 rounded-md bg-white px-4 py-1 text-black font-mono text-lg">
                          {isRecording ? formatTime(recordSeconds) : "0:00"}
                        </div>
                      </div>
                      <div className="w-[30vw]">
                        <p className="font-bold">
                          Steps to follow (for online meeting recordings):
                        </p>
                        <ul className="text-left gap-4 list-disc">
                          <li className="mt-4">
                            Ensure you and the interviewee join the meeting in
                            the same browser.
                          </li>
                          <li className="mt-4">
                            Click “REC” and choose "Meeting audio + Mic" then
                            choose the meeting tab when prompted; turn on “Share
                            tab audio.”
                          </li>
                          <li className="mt-4">
                            After sharing, recording begins. When done, stop
                            sharing and click the red “REC” button to stop.
                          </li>
                          <li className="mt-4">
                            The application will automatically upload the
                            recording for transcript generation.
                          </li>
                        </ul>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Bottom-right Close */}
              <div className="flex justify-end items-center pb-5 pr-8 gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => setOpenRecordingModal(false)}
                  className="flex items-center gap-3"
                >
                  <span>Close</span>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white text-xs">
                    X
                  </span>
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Calendar Dailog Modal */}
      <Dialog
        open={openInterviewScheduleModal}
        onClose={setOpenInterviewScheduleModal}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <DialogPanel
              transition
              className="relative mx-auto w-full max-w-5xl transform overflow-hidden bg-[#294563] text-white shadow-2xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              {/* Header row */}
              <div className="flex items-center justify-between px-8 pt-6">
                <div className="bg-white px-6 py-3 font-semibold text-black rounded-sm">
                  Book Interview
                </div>

                <div className="bg-white text-sm text-black rounded-sm flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  {/* Left side: current selection summary */}

                  {/* Right side: selector */}
                  <div className="flex flex-col items-start gap-1 md:items-end">
                    <select
                      id="interviewee-select"
                      className="w-full md:w-64 rounded-sm border border-gray-300 bg-white px-2 py-1.5 text-md text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      value={selectedInter?.intervieweeId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const found = intervieweeList.find(
                          (inter) => String(inter.intervieweeId) === value,
                        );
                        setSelectedInter(found || null);
                      }}
                    >
                      <option value="">-- Choose interviewee --</option>
                      {intervieweeList?.map((inter) => (
                        <option key={inter._id} value={inter.intervieweeId}>
                          {inter.name} — {inter.position} ({inter.department})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="px-4 pb-4">
                {/* Left: calendar + time */}
                <div className="flex flex-col w-full gap-6">
                  <div className="text-center">
                    <p className="text-sm tracking-wide">
                      Schedule Date &amp; Time
                    </p>
                    <p className="text-sm tracking-wide font-bold">
                      Please make sure to start the meeting in browser.
                    </p>
                    <div className="mx-auto mt-1 h-[2px] w-40 bg-white" />
                  </div>

                  <div className="mt-2 flex flex-col items-center gap-4">
                    {/* Slot toggles */}

                    {/* Month view calendar */}
                    <ScheduleCalendar
                      selectedInter={selectedInter}
                      interviewSlots={interviewSlots}
                      selectedDate={activeSlot.date}
                      onSelectDate={(date) =>
                        updateSlot(activeSlotIdx, { date })
                      }
                    />

                    {/* Time selection */}
                    <div className="flex justify-between items-center w-full">
                      <div className="flex direction-row flex-wrap justify-center gap-3">
                        {slotLabels.map((label, idx) => {
                          const slot = interviewSlots[idx];
                          const isActive = idx === activeSlotIdx;

                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => setActiveSlotIdx(idx)}
                              className={`rounded-md border px-4 py-2 text-left transition ${
                                isActive
                                  ? "border-[#ffc49b] bg-white/10 shadow-md"
                                  : "border-white/30 bg-white/5 hover:border-white/60"
                              }`}
                            >
                              <p className="text-[11px] uppercase tracking-wide text-white/70">
                                Slot {idx + 1}
                              </p>
                              <p className="text-sm font-semibold">
                                {slot.date
                                  ? formatDateFriendly(slot.date)
                                  : "Pick a date"}
                              </p>
                              <p className="text-[11px] text-white/80">
                                {slot.time
                                  ? formatTimeFriendly(slot.time)
                                  : "Time not set"}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      <div className="w-full max-w-md bg-white/95 text-black rounded-lg px-4 py-3 shadow">
                        <p className="mb-2 text-xs text-gray-600">
                          Selected for {slotLabels[activeSlotIdx].toLowerCase()}
                          :
                          <span className="font-medium">
                            &nbsp;
                            {activeSlot.date
                              ? formatDateFriendly(activeSlot.date)
                              : "Pick a date above"}
                          </span>
                        </p>

                        <div className="flex items-center justify-between gap-3">
                          <label className="text-sm font-medium">
                            Select time
                            <input
                              type="time"
                              value={activeSlot.time}
                              onChange={(e) =>
                                updateSlot(activeSlotIdx, {
                                  time: e.target.value,
                                })
                              }
                              className="mt-1 block w-32 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-black focus:outline-none focus:ring-2 focus:ring-[#294563]"
                            />
                          </label>

                          <button
                            type="button"
                            disabled={!allSlotsReady}
                            onClick={() => {
                              handleConfirmSchedule(
                                selectedInter?.assessmentId,
                                selectedInter?.intervieweeId,
                                interviewSlots,
                              );
                            }}
                            className={`rounded-md px-4 py-2 text-xs font-semibold ${
                              allSlotsReady
                                ? "bg-primary border-2 border-primary text-white hover:bg-white hover:text-primary"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            }`}
                          >
                            Submit Selection
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side: keep for future (notes / summary / anything) */}
                <div className="w-64">{/* your right-side content here */}</div>
              </div>

              {/* Bottom-right Close */}
              <button
                type="button"
                onClick={() => setOpenInterviewScheduleModal(false)}
                className="absolute top-0 right-0 flex items-center gap-3 bg-white"
              >
                <span className="flex h-7 w-7 text-primary items-center font-bold justify-center border border-white text-lg">
                  X
                </span>
              </button>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full"
      >
        {isTranscriptOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="relative w-full max-w-lg bg-white p-6 shadow-lg">
              <button
                type="button"
                className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                onClick={() => setIsTranscriptOpen(false)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <h2 className="mb-3 text-base font-semibold text-gray-900">
                Transcript
              </h2>

              <div className="max-h-80 overflow-y-auto whitespace-pre-wrap text-sm text-gray-700">
                {activeTranscript}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsTranscriptOpen(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <Dialog
          open={recordChoiceOpen}
          onClose={() => {
            recordInterRef.current = null;
            setRecordChoiceOpen(false);
          }}
          className="relative z-50"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/50" />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <DialogPanel className="bg-white w-full max-w-md rounded shadow-lg p-6 space-y-4">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Choose recording mode
              </DialogTitle>
              <p className="text-sm text-gray-700">
                Record only your microphone, or include meeting/system audio by
                sharing a tab/window with system audio enabled.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => handleRecordingChoice(true)}
                  className="w-full border border-[#063669] px-4 py-2 text-[#063669] font-semibold hover:bg-[#063669] hover:text-white"
                >
                  Mic only
                </button>
                <button
                  type="button"
                  onClick={() => handleRecordingChoice(false)}
                  className="w-full border border-[#063669] px-4 py-2 text-[#063669] font-semibold hover:bg-[#063669] hover:text-white"
                >
                  Meeting audio + Mic
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  recordInterRef.current = null;
                  setRecordChoiceOpen(false);
                }}
                className="text-sm text-gray-600 hover:underline self-start"
              >
                Cancel
              </button>
            </DialogPanel>
          </div>
        </Dialog>

        {showAssessmentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="relative w-[95%] max-w-4xl bg-white shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between border-b px-4 py-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Assessment File
                </h2>
                <button
                  type="button"
                  onClick={() => setShowAssessmentModal(false)}
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Body with iframe */}
              <div className="h-[70vh]">
                <iframe
                  src={showAssessmentModalFile}
                  title="Assessment File"
                  className="h-full w-full"
                />
              </div>

              {/* Footer (optional) */}
              <div className="flex justify-end gap-2 border-t px-4 py-2">
                <button
                  type="button"
                  onClick={() => setShowAssessmentModal(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <a
                  href={companyDetails?.assessmentFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" bg-primary1 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary1/90"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}

        <main className="relative w-full overflow-hidden">
          <div className="relative z-10 mx-auto w-full">
            {/* Frosted panel */}
            <section className="mt-5 sm:mt-6 lg:mt-8 border border-white/15 bg-[#11406c]/40 backdrop-blur-xs shadow-[0_10px_40px_rgba(0,0,0,0.35)] ring-1 ring-white/5">
              <div>
                {/* <AssessmentsMenu items={items} /> */}
                {/* Heading */}

                <h2 className="text-center relative items-center font-semibold text-xl sm:text-2xl lg:text-3xl bg-black p-4 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2">
                  <span>Active Assessment</span>
                  {policyTimer?.startedAt && (
                    <span className="text-sm sm:text-base text-white/80 font-bold absolute right-12">
                      AI Policy timer: {policyElapsed.minutes}m{" "}
                      {policyElapsed.seconds}s
                    </span>
                  )}
                  {assessTimer?.startedAt && (
                    <span className="text-sm sm:text-base text-white/80 font-bold absolute left-12">
                      Assessment timer: {assessElapsed.minutes}m{" "}
                      {assessElapsed.seconds}s
                    </span>
                  )}
                </h2>

                {/*Carousel navigation Bar*/}
                <div className="border-white text-white bg-[#000054] py-4 sm:py-6 px-4 sm:px-10">
                  <div className="flex flex-nowrap md:flex-wrap gap-3 sm:gap-0 text-sm sm:text-lg text-center overflow-x-auto md:overflow-visible">
                    <button
                      type="button"
                      onClick={() => handleSectionClick("company")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "company"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Company
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("addInterviews")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "addInterviews"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Add Interviews
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("interviewees")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "interviewees"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Interviewees
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("schedule")}
                      className={`min-w-[140px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "schedule"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Schedule Interviews
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("documents")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "documents"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Documents
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("customChat")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "customChat"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Custom Chat
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSectionClick("progress")}
                      className={`min-w-[120px] flex-1 cursor-pointer pb-2 border-b-2 whitespace-nowrap ${
                        activeTab === "progress"
                          ? "text-[#ffc49b] border-[#ffc49b]"
                          : "text-white border-white hover:text-[#ffc49b] hover:border-[#ffc49b]"
                      }`}
                    >
                      Progress
                    </button>
                  </div>
                </div>

                <Carousel
                  className="px-8 bg-[#000054]"
                  items={items}
                  setItems={setItems}
                  slideIndex={slideIndex}
                  setSlideIndex={setSlideIndex}
                >
                  {/* Slide 1 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <h3 className="text-white text-2xl py-2">
                        {company?.split("-")?.join(" ")?.toUpperCase()}
                      </h3>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Comapny Name
                        </label>
                        <input
                          value={companyDetails?.companyName}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Address
                        </label>
                        <input
                          value={companyDetails?.address1}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Address 2
                        </label>
                        <input
                          value={companyDetails?.address2}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          City
                        </label>
                        <input
                          value={companyDetails?.city}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Country
                        </label>
                        <select
                          value={companyDetails?.country}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        >
                          <option value="CA">Canada</option>
                          <option value="US">United States</option>
                        </select>
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Vertical
                        </label>
                        <select
                          value={companyDetails?.vertical}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        >
                          <option value="Vertical 1">Vertical 1</option>
                          <option value="Vertical 2">Vertical 2</option>
                        </select>
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Key Contact
                        </label>
                        <input
                          value={companyDetails?.keyContact}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Phone Number
                        </label>
                        <input
                          value={companyDetails?.phone}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Email Address
                        </label>
                        <input
                          value={companyDetails?.email}
                          disabled
                          className="border border-white text-black px-3 py-1 bg-white"
                        />
                      </div>

                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Position
                        </label>
                        <select
                          value={companyDetails?.posistion}
                          className="border border-white text-black px-3 py-1 bg-white"
                          disabled
                        >
                          <option value="Owner">Owner/President</option>
                          <option value="Ceo">CEO</option>
                          <option value="Sto">CTO</option>
                        </select>
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 2 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Add Interviewees
                      </p>

                      <p className="text-sm text-white mt-2 font-bold">
                        Enter name, email address and position of each
                        interviewee. Continue entering until you have completed
                        your list.
                      </p>

                      <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          Full name
                        </label>
                        <input
                          className="border border-primary text-primary1 px-1 py-1"
                          placeholder="Full Name"
                          value={ieName}
                          onChange={(e) => setIeName(e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          Email address
                        </label>
                        <input
                          className="border border-primary text-primary1 px-1 py-1"
                          placeholder="Email Address"
                          value={ieEmail}
                          onChange={(e) => setIeEmail(e.target.value)}
                        />
                      </div>

                      <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          Department
                        </label>
                        <select
                          className="border border-primary text-primary1 px-1 py-1 h-8"
                          value={ieDepartment}
                          onChange={(e) => setIeDepartment(e.target.value)}
                        >
                          <option value="">Department</option>
                          <option value="Executive Leadership">
                            Executive Leadership
                          </option>
                          <option value="Sales">Sales</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Customer Service">
                            Customer Service
                          </option>
                          <option value="Customer Service">
                            Service / Field Service
                          </option>
                          <option value="Technical Support">
                            Technical Support
                          </option>
                          <option value="IT">IT</option>
                          <option value="Engineering">Engineering</option>
                          <option value="Product Management">
                            Product Management
                          </option>
                          <option value="R&D">R&D</option>
                          <option value="QA / Quality Assurance">
                            QA / Quality Assurance
                          </option>
                          <option value="Operations">Operations</option>
                          <option value="Supply Chain / Logistics">
                            Supply Chain / Logistics
                          </option>
                          <option value="Procurement / Purchasing">
                            Procurement / Purchasing
                          </option>
                          <option value="Hr">HR</option>
                          <option value="Finance">Finance</option>
                          <option value="Accounting">Accounting</option>
                          <option value="Legal / Compliance">
                            Legal / Compliance
                          </option>
                          <option value="Administrative / Office Management">
                            Administrative / Office Management
                          </option>
                        </select>
                      </div>

                      <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          Position
                        </label>
                        <input
                          className="border border-primary text-primary1 px-1 py-1"
                          placeholder="Position"
                          value={iePosition}
                          onChange={(e) => setIePosition(e.target.value)}
                        />
                      </div>

                      {/* <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          Position
                        </label>
                        <select
                          className="border border-primary text-primary1 px-1 py-1 h-8"
                          value={iePosition}
                          onChange={(e) => setIePosition(e.target.value)}
                        >
                          <option value="">Position</option>
                          <option value="Manager">Manager</option>
                          <option value="Senior">Senior</option>
                          <option value="Lead">Lead</option>
                        </select>
                      </div> */}

                      <div className="flex flex-col space-y-1 mt-4">
                        <label className="block text-lg font-medium text-white">
                          AI Polcies & Guidelines
                        </label>
                        <select
                          className="border border-primary text-primary1 px-1 py-1 h-8"
                          value={aiPolocyGuidelines}
                          onChange={(e) =>
                            setAiPolocyGuidelines(e.target.value)
                          }
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>

                      <button
                        disabled={loading}
                        onClick={handleAddInterviewee}
                        className="mt-8 w-full justify-center bg-white px-5 py-2 text-sm sm:text-base font-semibold text-black shadow-sm border-2 border-primary1 hover:bg-[#285172] hover:text-white"
                      >
                        {loading ? "ADDING..." : "ADD"}
                      </button>
                    </div>
                  </Carousel.Item>
                  {/* Slide 3 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Interviewee List
                      </p>
                      <div className="flex flex-col gap-2 mt-8 h-[66vh] overflow-auto">
                        {intervieweeList?.length ? (
                          intervieweeList.map((item, i) => {
                            const isActive = i === selectedIdx;
                            return (
                              <div
                                key={
                                  item?.id ??
                                  item?._id ??
                                  `${labelFor(item, i)}-${i}`
                                }
                                //onClick={() => setSelectedIdx(i)}
                                className={[
                                  "p-2 relative text-center text-lg border transition-colors bg-[#5066dd]",
                                  isActive
                                    ? "bg-white text-primary1 border-primary1"
                                    : "bg-primary1 text-white ",
                                ].join(" ")}
                              >
                                <TrashIcon
                                  className="absolute w-6 right-2 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    const ok = window.confirm(
                                      "This can't be undone. Are you sure?",
                                    );
                                    if (!ok) return;
                                    handleDeleteInterviewee(item);
                                  }}
                                />
                                {item?.name}{" "}
                                {item?.aiPolocyGuidelines === "Yes"
                                  ? " - AI Policies & Guidelines"
                                  : null}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-sm text-gray-500 px-2">
                            No interviewees yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 4 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Interview Schedule/Calendar
                      </p>
                      <CalendarDateRangeIcon
                        className="w-28 sm:w-40 md:w-56 text-white mx-auto"
                        aria-hidden="true"
                      />
                      <div className="flex-col text-sm">
                        <p className="flex direction-row items-center justify-between bg-white py-2 px-4">
                          <span className="text-gray-900 ">
                            Total Number of Interviews
                          </span>
                          <span className="text-gray-900">
                            {intervieweeList?.length}
                          </span>
                        </p>
                        <p className="flex mt-4 direction-row items-center justify-between bg-white py-2 px-4">
                          <span className="text-gray-900 ">
                            Total Interviews Scheduled
                          </span>
                          <span className="text-gray-900">
                            {interviewDateSelected}
                          </span>
                        </p>
                        <p className="flex mt-4 direction-row items-center justify-between bg-white py-2 px-4">
                          <span className="text-gray-900 ">
                            Review Interview List
                          </span>
                          <span className="text-gray-900 hover:underline cursor-pointer">
                            click here
                          </span>
                        </p>
                        {intervieweeList?.length > 0 && (
                          <p className="flex mt-4 direction-row items-center justify-between bg-white py-2 px-4">
                            <span className="text-gray-900 ">
                              Group Booking
                            </span>
                            <span
                              onClick={() => {
                                setSelectedInter(intervieweeList[0]);
                                setOpenInterviewScheduleModal(true);
                              }}
                              className="text-gray-900 hover:underline cursor-pointer"
                            >
                              click here
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 5 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Assessment Documents
                      </p>

                      <div className="bg-primary py-2 px-4 sm:px-8 mb-2">
                        <div className="p-2 bg-white">
                          <div className="border-2 border-primary1 p-2 text-center bg-white">
                            <h3 className="text-primary1 text-md py-2 text-center">
                              QUESTIONAIRE
                            </h3>
                            <p className="text-center text-primary1 text-xs">
                              Ready for Download
                            </p>
                            <DocumentIcon
                              aria-hidden="true"
                              className="w-14 h-14 sm:w-20 sm:h-20 text-primary1 text-center block mx-auto"
                            />
                            <div
                              onClick={() => {
                                const a = document.createElement("a");
                                a.href =
                                  "https://res.cloudinary.com/dcfotquxt/raw/upload/v1758030172/Ai-AssEss/GoWest_Employee_AI_Readiness_Interview_Questions_x0oy8c.docx";
                                a.rel = "noopener";
                                a.download =
                                  "GoWest_Employee_AI_Readiness_Interview_Questions.docx";
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                              }}
                              className="px-4 py-1 w-full text-xs bg-primary1 text-white border-primary border-2 text-center font-bold hover:bg-white hover:text-primary1 cursor-pointer"
                            >
                              Download
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* ...repeat the two other document cards exactly as you had... */}
                      {/* Ai Policies & Guidelines */}
                      <div className="bg-primary py-2 px-4 sm:px-8 mb-2">
                        <div className="p-2 bg-white">
                          <div className="border-2 border-primary1 p-2 text-center bg-white">
                            <h3 className="text-primary1 text-md py-2 text-center">
                              Ai Policies & Guidelines
                            </h3>
                            <p className="text-center text-primary1 text-xs">
                              Ready for Download
                            </p>
                            <DocumentIcon
                              aria-hidden="true"
                              className="w-14 h-14 sm:w-20 sm:h-20 text-primary1 text-center block mx-auto"
                            />
                            <div
                              onClick={() => {
                                const rawUrl =
                                  "https://res.cloudinary.com/dcfotquxt/image/upload/v1772945998/Ai-AssEss/AI_Policies_Questionaire_V_1_02262026_m0wiia.pdf";
                                const downloadUrl = rawUrl.replace(
                                  "/upload/",
                                  "/upload/fl_attachment/",
                                );
                                const a = document.createElement("a");
                                a.href = downloadUrl;
                                a.rel = "noopener";
                                a.download =
                                  "Ai Policies and Guidelines Document.pdf";
                                a.target = "_blank";
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                              }}
                              className="px-4 py-1 w-full text-xs bg-primary1 text-white border-primary border-2 text-center font-bold hover:bg-white hover:text-primary1 cursor-pointer"
                            >
                              Download
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 6 */}
                  <Carousel.Item>
                    <div className="border-2 relative p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Customer Documents
                      </p>

                      {/* Assessment Findings */}
                      <div className="bg-primary py-2 px-4 sm:px-8 mb-2 relative">
                        {!companyDetails?.assessmentFile ? (
                          <div className="absolute top-0 right-0 opacity-50 bg-gray-500 w-full h-full z-50"></div>
                        ) : null}
                        <div className="p-2 bg-white">
                          <div className="relative border-2 border-primary1 p-2 text-center bg-white">
                            {companyDetails?.assessmentFile ? (
                              <TrashIcon
                                aria-hidden="true"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAssessmentDocument("assessment");
                                }}
                                className="w-5 hover:text-red-900 cursor-pointer absolute top-0 right-0 text-red-700 text-center block mx-auto"
                              />
                            ) : null}
                            <h3 className="text-primary1 text-md py-2 text-center">
                              Assessment Findings
                            </h3>
                            <p className="text-center text-primary1 text-xs">
                              Ready for Download
                            </p>
                            <div className="relative">
                              <DocumentIcon
                                aria-hidden="true"
                                className="w-14 h-14 sm:w-20 sm:h-20 text-primary1 text-center block mx-auto"
                              />

                              {companyDetails?.assessmentFile ? (
                                <CheckCircleIcon
                                  aria-hidden="true"
                                  className="absolute w-6 h-6 m-auto cursor-pointer right-0 top-4 left-0 bottom-0 text-green-600 opacity-60 pointer-events-none"
                                />
                              ) : null}
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setShowAssessmentModalFile(
                                  companyDetails?.assessmentFile,
                                );
                                setShowAssessmentModal(true);
                              }}
                              className="px-4 py-1 w-full text-xs bg-primary1 border-primary border-2 text-center text-white font-bold hover:bg-white hover:text-primary1 cursor-pointer"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* ...repeat the two other document cards exactly as you had... */}
                      {/* Ai Policies & Guidelines */}
                      <div className="bg-primary py-2 px-4 sm:px-8 mb-2 relative">
                        {!companyDetails?.assessmentAiPolicyPdf ? (
                          <div className="absolute top-0 right-0 opacity-50 bg-gray-500 w-full h-full z-50"></div>
                        ) : null}
                        <div className="p-2 bg-white">
                          <div className="relative border-2 border-primary1 p-2 text-center bg-white">
                            {companyDetails?.assessmentAiPolicyPdf ? (
                              <TrashIcon
                                aria-hidden="true"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAssessmentDocument("policy");
                                }}
                                className="w-5 hover:text-red-900 cursor-pointer absolute top-0 right-0 text-red-700 text-center block mx-auto"
                              />
                            ) : null}
                            <h3 className="text-primary1 text-md py-2 text-center">
                              Ai Policies & Guidelines
                            </h3>
                            <p className="text-center text-primary1 text-xs">
                              Ready for Download
                            </p>
                            <div className="relative">
                              <DocumentIcon
                                aria-hidden="true"
                                className="w-14 h-14 sm:w-20 sm:h-20 text-primary1 text-center block mx-auto"
                              />
                              {companyDetails?.assessmentAiPolicyPdf ? (
                                <CheckCircleIcon
                                  aria-hidden="true"
                                  className="absolute w-6 h-6 m-auto cursor-pointer right-0 top-4 left-0 bottom-0 text-green-600 opacity-60 pointer-events-none"
                                />
                              ) : null}
                            </div>

                            <div
                              onClick={() => {
                                setShowAssessmentModalFile(
                                  companyDetails?.assessmentAiPolicyPdf,
                                );
                                setShowAssessmentModal(true);
                              }}
                              className="px-4 py-1 w-full text-xs bg-primary1 text-white border-primary border-2 text-center  font-bold hover:bg-white hover:text-primary1 cursor-pointer"
                            >
                              Download
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 7 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Interviewes
                      </p>
                      <div className="border-0 flex flex-col">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Stat
                            label="Interviews to complete"
                            value={pendingInterviews}
                            bg="#063669"
                          />
                          <Stat
                            label="Completed Interviews"
                            value={completedInterviews}
                            bg="#4da72e"
                          />
                        </div>

                        <div className="flex w-full gap-4 direction-row h-full flex-col">
                          <div className="w-full pl-0 pt-2 pb-2 flex gap-2 flex-col ">
                            <div className="grid gap-4">
                              {intervieweeList.map((inter, i) => (
                                <div
                                  className="flex direction-row gap-2 flex-col sm:flex-row"
                                  key={inter?._id ?? inter?.intervieweeId ?? i}
                                >
                                  <div className="w-full relative bg-white p-2 pl-9 flex direction-row gap-2 overflow-hidden justify-between">
                                    <p className="text-xs p-3 left-1 rounded-full h-4 w-4 absolute font-bold text-white bg-primary1 items-center flex justify-center">
                                      {i + 1}
                                    </p>
                                    <div className="relative w-full">
                                      <TrashIcon
                                        aria-hidden="true"
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          const ok = window.confirm(
                                            "This can't be undone. Are you sure?",
                                          );
                                          if (!ok) return;
                                          handleDeleteInterviewee(inter);
                                        }}
                                        className={[
                                          "size-6 cursor-pointer absolute right-1 bottom-1",
                                          deletingId ===
                                          (inter?.intervieweeId || inter?._id)
                                            ? "opacity-50 pointer-events-none text-red-400"
                                            : "text-red-500 hover:opacity-80",
                                        ].join(" ")}
                                      />
                                      {inter?.interviewStatus == "completed" ? (
                                        <CheckCircleIcon
                                          aria-hidden="true"
                                          className="size-6 cursor-pointer absolute right-1 top-1 text-green-600 opacity-60 pointer-events-none"
                                        />
                                      ) : null}

                                      <div>
                                        <p className="text-sm font-bold text-black truncate max-w-[250px] sm:max-w-[250px]">
                                          {inter?.name}
                                          {inter?.aiPolocyGuidelines === "Yes"
                                            ? " - AI Policies & Guidelines"
                                            : null}
                                        </p>
                                        <p className="text-md text-primary underline">
                                          {inter?.email}
                                        </p>
                                        <p className="text-md text-black">
                                          {inter?.department} -{" "}
                                          {inter?.position}
                                        </p>
                                        {inter?.interviewSlots ? (
                                          inter?.interviewSlots[0]
                                            ?.confirmed ? (
                                            <p className="text-gray-900 text-sm">
                                              Interview Scheduled:{" "}
                                              {inter?.interviewSlots[0]?.date} (
                                              {inter?.interviewSlots[0]?.time})
                                            </p>
                                          ) : inter?.interviewSlots[1]
                                              ?.confirmed ? (
                                            <p className="text-gray-900 text-sm">
                                              Interview Scheduled:{" "}
                                              {inter?.interviewSlots[1]?.date} (
                                              {inter?.interviewSlots[1]?.time})
                                            </p>
                                          ) : null
                                        ) : null}
                                        {inter?.files?.map((file, i) => {
                                          const assessmentId =
                                            companyDetails?.assessmentId ||
                                            companyDetails?._id;
                                          const intervieweeId =
                                            inter?.intervieweeId || inter?._id;

                                          return (
                                            <div
                                              className="inline-flex items-center gap-1 mr-4"
                                              key={file.public_id || i}
                                            >
                                              <a
                                                href={file?.url || "#"}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-x-0.5 rounded-md bg-pink-100 px-2 py-1 text-xs font-medium text-pink-700"
                                              >
                                                {file?.format == "raw" ? (
                                                  <>
                                                    <PaperClipIconView />{" "}
                                                    File{" "}
                                                  </>
                                                ) : (
                                                  <>
                                                    <MicrophoneIconView />{" "}
                                                    Rec{" "}
                                                  </>
                                                )}
                                                {i + 1}{" "}
                                                {/* <FileIcon
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  //convert file?.url webm format to a text and console
                                                }}
                                              /> */}
                                                <button
                                                  onClick={(e) => {
                                                    e?.preventDefault();

                                                    const text =
                                                      file?.transcript?.text;
                                                    setActiveTranscript(
                                                      text ||
                                                        "No transcript available.",
                                                    );
                                                    setIsTranscriptOpen(true);
                                                  }}
                                                >
                                                  <DocumentTextIcon className="w-4 cursor-pointer" />
                                                </button>
                                                <button
                                                  type="button"
                                                  className="group relative -mr-1 size-3.5 rounded-sm hover:bg-pink-600/20"
                                                  onClick={(e) => {
                                                    e.preventDefault();

                                                    const ok = window.confirm(
                                                      "This can't be undone. Are you sure?",
                                                    );

                                                    if (!ok) return;

                                                    setFiles((prev) => {
                                                      // use state if present, otherwise fall back to inter.files
                                                      const prevFiles =
                                                        prev[intervieweeId] ??
                                                        inter.files ??
                                                        [];

                                                      // remove this file (by index)
                                                      const nextFiles =
                                                        prevFiles.filter(
                                                          (f) =>
                                                            f.public_id !==
                                                            file.public_id,
                                                        );

                                                      // PATCH backend with updated list
                                                      patchIntervieweeFiles({
                                                        assessmentId,
                                                        intervieweeId,
                                                        files: nextFiles,
                                                      }).catch((err) => {
                                                        console.error(
                                                          "PATCH failed:",
                                                          err,
                                                        );
                                                      });

                                                      // update local state
                                                      return {
                                                        ...prev,
                                                        [intervieweeId]:
                                                          nextFiles,
                                                      };
                                                    });
                                                  }}
                                                >
                                                  <svg
                                                    viewBox="0 0 14 14"
                                                    className="size-3.5 stroke-pink-800/50 group-hover:stroke-pink-800/75"
                                                  >
                                                    <path d="M4 4l6 6m0-6l-6 6" />
                                                  </svg>
                                                  <span className="absolute -inset-1" />
                                                </button>
                                              </a>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {(() => {
                                      const id =
                                        inter?.intervieweeId ||
                                        inter?._id ||
                                        inter?.email ||
                                        inter?.name;
                                      const isRecording = recordingFor === id;
                                      const isUploading = uploadingFor === id;

                                      return (
                                        <div className="flex gap-2 sm:gap-3 mt-2 sm:mt-0 sm:flex-col md:flex-row">
                                          <div
                                            onClick={() => {
                                              setOpenInterviewScheduleModal(
                                                true,
                                              );

                                              setSelectedInter(inter);
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            className={[
                                              "border relative cursor-pointer bg-primary1 px-2 py-2 flex flex-col items-center gap-1 w-24 sm:w-20 text-center justify-center",
                                              isUploading
                                                ? "opacity-60 pointer-events-none"
                                                : "",
                                            ].join(" ")}
                                          >
                                            {inter?.interviewSlots ? (
                                              inter?.interviewSlots[0]
                                                ?.confirmed ||
                                              inter?.interviewSlots[1]
                                                ?.confirmed ? (
                                                <CheckCircleIcon className="w-4 absolute right-0 top-0" />
                                              ) : null
                                            ) : null}

                                            <CalendarDateRangeIcon className="w-12 text-white" />

                                            <p className="font-bold text-white text-xs">
                                              Schedule
                                            </p>
                                          </div>
                                          <div
                                            onClick={() => {
                                              setOpenRecordingModal(true);
                                              setFiles(inter?.files);
                                              setSelectedInter(inter);
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            className={[
                                              "border cursor-pointer bg-primary1 px-2 py-2 flex flex-col items-center gap-1 w-24 sm:w-20 text-center justify-center",
                                              isUploading
                                                ? "opacity-60 pointer-events-none"
                                                : "",
                                            ].join(" ")}
                                          >
                                            <MicrophoneIcon className="w-6 text-white" />

                                            <p className="font-bold text-white text-xs">
                                              REC
                                            </p>
                                            <p className="bg-white rounded p-2 py-0 text-sm text-black text-xs">
                                              {isRecording
                                                ? formatTime(recordSeconds)
                                                : "0:00"}
                                            </p>
                                          </div>
                                          <div
                                            onClick={() => {
                                              setFiles(inter?.files);
                                              setSelectedInter(inter);
                                              handleUploadPdf(inter);
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            className={[
                                              "border relative cursor-pointer bg-primary1 px-2 py-2 flex flex-col items-center gap-1 w-24 sm:w-20 text-center justify-center",
                                            ].join(" ")}
                                          >
                                            <CloudArrowUpIcon className="w-6 text-white" />

                                            <p className="font-bold text-white text-xs">
                                              Upload
                                            </p>
                                            <p className="  text-white text-xs">
                                              (.pdf, .docx)
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ))}
                              {!loading && intervieweeList.length === 0 && (
                                <div className="text-gray-500 text-sm px-2 col-span-2">
                                  No interviewees yet.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex direction-row w-full gap-2">
                          {!companyDetails?.assessmentFile &&
                          !assessTimer?.startedAt ? (
                            <button
                              onClick={() => {
                                handleSubmitAssessment(canSubmitAssessment);
                              }}
                              className={`w-full text-center items-center flex justify-center ${
                                canSubmitAssessment
                                  ? "bg-[#5066dd] cursor-pointer"
                                  : "bg-[#3e414f] cursor-not-allowed"
                              }  h-9 font-bold text-xs`}
                            >
                              Submit AI Assessment Findings
                            </button>
                          ) : null}

                          {!companyDetails?.assessmentAiPolicyPdf &&
                          !policyTimer?.startedAt ? (
                            <button
                              onClick={() => {
                                handleSubmitAiPolicyAssessment(
                                  canSubmitAiPolicyAssessment,
                                );
                              }}
                              className={`w-full text-center items-center flex justify-center ${
                                canSubmitAiPolicyAssessment
                                  ? "bg-[#5066dd] cursor-pointer"
                                  : "bg-[#3e414f] cursor-not-allowed"
                              }  h-9 font-bold text-xs`}
                            >
                              Submit AI Policies & Guidelines
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Carousel.Item>
                  {/* Slide 8 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto relative">
                      <div className="absolute top-0 right-0 opacity-70 bg-gray-500 w-full h-full"></div>
                      <p className="text-white font-bold text-2xl absolute gap-2 flex text-center justify-center items-center top-0 right-0 left-0 bottom-0">
                        <ChatBubbleLeftRightIcon className="text-white w-16" />
                        <span>COMING SOON</span>
                      </p>
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Custom Chat
                      </p>
                      <p className="text-sm text-white mt-2 font-bold">
                        You can ask any questions specific to your assessment.
                      </p>
                      <textarea
                        id="comment"
                        name="comment"
                        placeholder="Ask a question ?"
                        rows={6}
                        className="block mt-2 mb-4 border-primary1 border-2 w-full bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-black-500 rounded-lg focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-customBlack sm:text-sm/6"
                        defaultValue=""
                      />
                    </div>
                  </Carousel.Item>
                  {/* Slide 9 */}
                  <Carousel.Item>
                    <div className="border-2 p-3 sm:p-4 bg-white/5 h-auto md:h-[60vh] lg:h-[60vh] overflow-auto">
                      <p className="w-full py-1 bg-white text-center text-black text-sm sm:text-base font-bold mb-3">
                        Assessment Progress
                      </p>
                      <div className="h-full w-[100%] flex flex-col gap-2 realtive">
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <p className="w-[80%] bg-white  p-4 m-0 text-black text-sm">
                            Assessment Questionnaire Downloaded
                          </p>
                          <div
                            className="w-[20%] bg-white items-center font-bold text-center justify-center cursor-pointer flex m-0"
                            onClick={() => setShowInfo((prev) => !prev)}
                          >
                            {showInfo ? (
                              <InformationCircleIcon
                                aria-hidden="true"
                                className="size-6 text-black"
                              />
                            ) : (
                              <CheckCircleIcon
                                aria-hidden="true"
                                className="size-6 text-black"
                              />
                            )}
                          </div>
                        </div>
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <p className="w-[80%] bg-white  p-4 m-0 text-black text-sm">
                            Assessment Questionnaire Downloaded
                          </p>
                          <div className="w-[20%] items-center flex justify-center bg-white text-center font-bold py-4 text-black m-0 text-sm">
                            12
                          </div>
                        </div>
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <p className="w-[80%] bg-white  p-4 m-0 text-black text-sm">
                            Completed Interviews
                          </p>
                          <div className="w-[20%] flex items-center justify-center bg-white text-black font-bold text-center py-4 text-center m-0 text-sm">
                            {completedInterviews}
                          </div>
                        </div>
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <p className="w-[80%] bg-white  p-4 m-0 text-black text-sm">
                            Generate Report
                          </p>
                          <div
                            className="w-[20%] flex items-center bg-white p-4 text-white text-center justify-center cursor-pointer flex m-0"
                            onClick={() => setShowInfo1((prev) => !prev)}
                          >
                            {showInfo1 ? (
                              <InformationCircleIcon
                                aria-hidden="true"
                                className="size-6 text-black"
                              />
                            ) : (
                              <CheckCircleIcon
                                aria-hidden="true"
                                className="size-6 text-black"
                              />
                            )}
                          </div>
                        </div>
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <p className="w-[80%] bg-white  p-4 m-0 text-black text-sm">
                            Recommended Ai Applications
                          </p>
                          <div className="w-[20%] items-center flex justify-center bg-white text-black font-bold py-4 text-center m-0 text-sm">
                            1
                          </div>
                        </div>
                        <div className="w-[100%] flex direction-row relative gap-4">
                          <div className="w-[80%] bg-white text-white p-4 m-0 text-black text-sm relative overflow-hidden">
                            <div
                              style={{
                                width: `${
                                  Number(
                                    (completedInterviews /
                                      (completedInterviews +
                                        pendingInterviews)) *
                                      100,
                                  )
                                    .toFixed(2)
                                    .split(".")[0]
                                }%`,
                              }}
                              className={`absolute top-0 left-2 h-[80%] m-auto bottom-0 bg-[#4da72e] z-0`}
                            ></div>
                            <p className="relative z-10 text-black">
                              Overall Progress
                            </p>
                          </div>
                          <div className="w-[20%] items-center flex justify-center bg-white font-bold text-black py-4 text-center m-0 text-sm">
                            {
                              Number(
                                (completedInterviews /
                                  (completedInterviews + pendingInterviews)) *
                                  100,
                              )
                                .toFixed(2)
                                .split(".")[0]
                            }
                            %
                          </div>
                        </div>
                      </div>
                    </div>
                  </Carousel.Item>
                </Carousel>
              </div>
            </section>
          </div>
        </main>
      </motion.div>
    </div>
  );
}

/* ----------------- helpers ----------------- */

/* helpers */
function unslugToName(slug) {
  return String(slug || "")
    .replace(/-/g, " ")
    .toUpperCase();
}

function Stat({ label, value, bg }) {
  return (
    <div className="flex direction-row relative gap-2 justify-between w-full mb-2">
      <p
        className="w-[90%] py-2 pl-2 m-0 text-white text-xs font-bold"
        style={{ background: bg }}
      >
        {label}
      </p>
      <p
        className="w-[10%] py-2 text-center m-0 text-white text-sm font-bold"
        style={{ background: bg }}
      >
        {value}
      </p>
    </div>
  );
}
